/**
 * ONE-TIME migration: legacy localStorage device key → non-extractable
 * WebCrypto key.
 *
 * Before the hardened WebCrypto path existed, the Ed25519 device key was kept
 * as raw bytes in localStorage. This module reuses that existing key material
 * so the derived `deviceId` / public key stay **identical** — the gateway keeps
 * recognizing the device and no re-pairing is triggered.
 *
 * Factory pattern: hosts supply the legacy storage key string (which differs
 * between `paradex_terminal_device` and `paradigm_terminal_device`). The
 * migrator can be deleted wholesale once the legacy key has aged out.
 */

import type { StoredCryptoKeyDevice } from './device-identity';

/**
 * Fixed PKCS#8 DER prefix for an Ed25519 private key (RFC 8410), as hex. The
 * full encoding is this 16-byte header followed by the raw 32-byte seed, so we
 * can reconstruct an importable PKCS#8 key from the legacy raw seed alone.
 */
const PKCS8_ED25519_PREFIX_HEX = '302e020100300506032b657004220420';

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

const PKCS8_ED25519_PREFIX = hexToBytes(PKCS8_ED25519_PREFIX_HEX);

interface LegacyStoredDevice {
  readonly id: string;
  readonly privateKey: string; // base64 of raw 32-byte seed
  readonly publicKey: string; // base64 of raw 32-byte public key
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.codePointAt(i)!;
  return out;
}

function readLegacyDevice(legacyStorageKey: string): LegacyStoredDevice | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(legacyStorageKey);
  } catch {
    return null; // localStorage unavailable (e.g. private mode)
  }
  if (raw == null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<LegacyStoredDevice>;
    if (
      typeof parsed.id === 'string' &&
      typeof parsed.privateKey === 'string' &&
      typeof parsed.publicKey === 'string'
    ) {
      return parsed as LegacyStoredDevice;
    }
  } catch {
    // Corrupt JSON — nothing to migrate.
  }
  return null;
}

export interface LegacyDeviceKeyMigrator {
  /**
   * Import a legacy localStorage device key into a non-extractable WebCrypto
   * record, preserving the existing `id` and `publicKey`.
   *
   * Returns `null` when there is no valid legacy key to migrate.
   */
  migrateLegacyDeviceKey(): Promise<StoredCryptoKeyDevice | null>;
  /** Remove the legacy raw key from localStorage after a successful migration. */
  clearLegacyDeviceKey(): void;
}

export interface LegacyDeviceKeyMigratorConfig {
  /** Legacy localStorage key — must match the historical value exactly. */
  legacyStorageKey: string;
}

export function createLegacyDeviceKeyMigrator(
  config: LegacyDeviceKeyMigratorConfig,
): LegacyDeviceKeyMigrator {
  const { legacyStorageKey } = config;

  return {
    async migrateLegacyDeviceKey(): Promise<StoredCryptoKeyDevice | null> {
      const legacy = readLegacyDevice(legacyStorageKey);
      if (legacy == null) return null;

      const seed = base64ToBytes(legacy.privateKey);
      if (seed.length !== 32) return null; // not a raw Ed25519 seed

      const pkcs8 = new Uint8Array(PKCS8_ED25519_PREFIX.length + seed.length);
      pkcs8.set(PKCS8_ED25519_PREFIX, 0);
      pkcs8.set(seed, PKCS8_ED25519_PREFIX.length);

      try {
        const privateKey = await crypto.subtle.importKey(
          'pkcs8',
          pkcs8,
          { name: 'Ed25519' },
          false, // non-extractable
          ['sign'],
        );
        return {
          id: legacy.id,
          publicKey: legacy.publicKey,
          privateKey,
        };
      } catch {
        return null;
      }
    },

    clearLegacyDeviceKey(): void {
      try {
        localStorage.removeItem(legacyStorageKey);
      } catch {
        // localStorage unavailable — nothing to clear.
      }
    },
  };
}
